import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, ArrowRight, CheckCircle2, XCircle, Target, GitBranch, FileCheck2, Scale, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fade, Eyebrow, usePageMeta } from "@/components/SiteLayout";

const PROOF_STATS = [
  { stat: "100%", label: "of published JCA assessments dissected — within days of release" },
  { stat: "9", label: "levels of evidence hierarchy behind every readiness score" },
  { stat: "6+", label: "EU markets analysed country-by-country, never on average" },
  { stat: "0", label: "raw AI outputs delivered — every deliverable consultant-validated" },
];

const AUTHORITY_POINTS = [
  "Evidence planning from Phase II onwards",
  "Multiple therapeutic areas",
  "AI-supported · Consultant-validated",
  "Continuously updated as new JCAs are released",
];

const SCOPE_WAVES = [
  { year: "2025", scope: "Oncology & ATMPs", status: "In scope now", active: true },
  { year: "2028", scope: "Orphan medicines", status: "3 evidence-planning windows away", active: false },
  { year: "2030", scope: "All new medicines", status: "Every asset, every submission", active: false },
];

const CAPABILITIES = [
  { icon: Target, title: "PICO Readiness", desc: "Anticipate the populations, comparators and outcomes Member States will demand — before they are announced — so your evidence answers the questions that will actually be asked." },
  { icon: Scale, title: "Comparator Readiness", desc: "Know which comparators matter in which markets — and whether your evidence against them will hold up." },
  { icon: ShieldCheck, title: "Evidence Gap Identification", desc: "See the weaknesses in your evidence package while there is still time to close them — not after assessors find them." },
  { icon: Compass, title: "Evidence Strategy & Roadmap", desc: "Every gap comes paired with a strategic recommendation and its expected impact on readiness — so leadership knows exactly what to fix, in what order, and why." },
  { icon: GitBranch, title: "Comparative Evidence Readiness", desc: "Understand whether your indirect comparisons will survive assessor scrutiny — before your strategy depends on them." },
  { icon: FileCheck2, title: "Scientific Defensibility", desc: "Know whether your evidence package is transparent, traceable and reproducible enough to withstand Article 9 completeness scrutiny." },
];

const ReadinessCard = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_30px_80px_-24px_rgba(15,23,42,0.22)]" data-testid="hero-readiness-card">
    <div className="flex items-center justify-between">
      <div className="text-sm text-slate-500">Sample Executive Dashboard · demo asset</div>
      <span className="rounded-md bg-amber-50 text-amber-700 border border-amber-200/60 px-2.5 py-1 text-xs font-medium">Risk · Moderate</span>
    </div>
    <div className="mt-6 flex items-end gap-3">
      <div className="text-6xl font-display font-semibold tracking-tight text-slate-900">68</div>
      <div className="mb-2 text-slate-500">/ 100 JCA Readiness</div>
    </div>
    <div className="mt-6 space-y-3.5">
      {[["Evidence Defensibility", 72], ["Comparator Coverage", 84], ["ITC Feasibility", 58], ["Patient-Relevant Outcomes", 48], ["Transparency & Traceability", 81]].map(([l, v], i) => (
        <div key={l}>
          <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{l}</span><span className="font-medium text-slate-900">{v}%</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <motion.div initial={{ width: 0 }} whileInView={{ width: `${v}%` }} viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.3 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`h-full rounded-full ${v >= 80 ? "bg-emerald-500" : v >= 60 ? "bg-blue-600" : "bg-red-500"}`} />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-6 rounded-lg bg-slate-900 px-4 py-3 flex items-center justify-between">
      <span className="text-[10px] font-semibold tracking-widest uppercase text-blue-400">Governance Recommendation</span>
      <span className="text-sm font-medium text-white">Proceed with Evidence Generation</span>
    </div>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  usePageMeta(
    "JCA Matrix™ — AI-enabled, Expert-led JCA Consulting | MarksMan Healthcare",
    "Find the weaknesses in your evidence before European JCA assessors do. AI-powered analysis, validated by senior HEOR consultants — one verdict, one roadmap, board-ready."
  );
  return (
    <>
      {/* Hero */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.5]" />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/60 to-white" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div variants={fade} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 mb-8">
              <Compass className="h-3.5 w-3.5 text-blue-600" /> MarksMan Healthcare · HEOR & Market Access Consulting
            </motion.div>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              AI-enabled, Expert-led JCA Consulting for <span className="text-blue-600">Europe</span>
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed">
              JCA Matrix™ is MarksMan Healthcare's proprietary JCA Readiness & Strategy Platform. Our
              consultants use it to find the weaknesses in your evidence before European assessors do — and give
              your leadership team a clear, defensible plan to fix them while there is still time.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-btn" onClick={() => navigate("/contact")} className="group bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-12 px-6 text-base font-medium shadow-sm transition-colors duration-200">
                Request a JCA Evidence Stress Test <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Button>
              <Button variant="outline" data-testid="hero-secondary-btn" onClick={() => navigate("/approach")} className="rounded-lg h-12 px-6 text-base border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                See Our Approach
              </Button>
            </motion.div>
          </div>
          <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="lg:col-span-5">
            <ReadinessCard />
          </motion.div>
        </div>
      </section>

      {/* Executive answers */}
      <section className="py-20 border-b border-slate-200" data-testid="executive-answers">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Eyebrow>The essentials, in one minute</Eyebrow>
          <div className="mt-8 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white shadow-sm">
            {[
              { q: "What is JCA Matrix?", a: "MarksMan Healthcare's proprietary, AI-powered JCA Readiness & Strategy Platform — used exclusively by our consultants to power every engagement. It is never sold or licensed as software." },
              { q: "Who is it for?", a: "Global HEOR, Market Access, Medical Affairs, Evidence Generation and leadership teams preparing assets for Europe's Joint Clinical Assessment." },
              { q: "What problem does it solve?", a: "JCA exposes evidence gaps that took years to form — after it is too late to fix them. We surface those gaps while your team can still act on them." },
              { q: "Why is it different?", a: "Conventional consulting is slow and manual. AI tools lack judgment. We combine both: AI-scale analysis, validated and interpreted by senior HEOR consultants." },
              { q: "What will my leadership team receive?", a: "A clear verdict on your readiness, a prioritized evidence roadmap, and a board-ready decision package — presented and defended in person." },
            ].map((item, i) => (
              <motion.div key={item.q} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="grid md:grid-cols-12 gap-2 md:gap-8 px-6 sm:px-8 py-5" data-testid={`executive-answer-${i}`}>
                <div className="md:col-span-4 font-display font-semibold tracking-tight text-slate-900">{item.q}</div>
                <div className="md:col-span-8 text-slate-600 leading-relaxed text-sm sm:text-base">{item.a}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority + proof */}
      <section className="py-16 border-b border-slate-200" data-testid="authority-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0}
            className="font-display text-2xl sm:text-3xl font-semibold tracking-tight max-w-3xl mx-auto">
            Trusted by teams preparing Europe's most complex evidence submissions
          </motion.h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="proof-stats">
            {PROOF_STATS.map((p, i) => (
              <motion.div key={p.stat + i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-6" data-testid={`proof-stat-${i}`}>
                <div className="font-display text-4xl font-bold tracking-tight text-blue-600">{p.stat}</div>
                <p className="mt-2 text-sm text-slate-600 leading-snug">{p.label}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-4">
            {AUTHORITY_POINTS.map((t, i) => (
              <motion.span key={t} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-700" data-testid={`authority-point-${i}`}>
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {t}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* JCA scope timeline — urgency */}
      <section className="py-16 bg-slate-900 border-b border-slate-800" data-testid="scope-timeline">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-400">Is your asset in scope?</div>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                JCA isn't coming. <span className="text-blue-400">It's here — and its scope only widens.</span>
              </h2>
              <p className="mt-4 text-slate-400 text-sm leading-relaxed">
                Evidence decisions made in Phase II determine what you can defend at assessment. If your asset launches
                after 2028, its JCA evidence window is open right now.
              </p>
              <button onClick={() => navigate("/contact")} className="group mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors" data-testid="scope-timeline-cta">
                Check your exposure <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </div>
            <div className="lg:col-span-7 space-y-3">
              {SCOPE_WAVES.map((w, i) => (
                <motion.div key={w.year} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className={`rounded-xl border px-5 py-4 flex items-center gap-5 ${w.active ? "border-blue-500 bg-blue-500/10" : "border-slate-700 bg-slate-800/50"}`}
                  data-testid={`scope-wave-${i}`}>
                  <span className={`font-display text-2xl font-bold ${w.active ? "text-blue-400" : "text-slate-500"}`}>{w.year}</span>
                  <div className="flex-1">
                    <div className="font-medium text-white">{w.scope}</div>
                    <div className="text-xs text-slate-400">{w.status}</div>
                  </div>
                  {w.active && <span className="rounded-full bg-blue-500 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">Active</span>}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic capabilities */}
      <section className="py-24" data-testid="strategic-capabilities">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>Strategic capabilities</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Ready for the questions Europe <span className="text-blue-600">hasn't asked yet</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              JCA requirements — especially PICOs — keep evolving with every assessment. Our engagements build your
              organisation's readiness across six strategic dimensions:
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map((c, i) => (
              <motion.div key={c.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="group rounded-2xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 hover:border-blue-200 transition-all duration-300"
                data-testid={`capability-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center transition-colors duration-300 group-hover:bg-blue-600 group-hover:border-blue-600">
                  <c.icon className="h-5 w-5 text-blue-600 transition-colors duration-300 group-hover:text-white" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold tracking-tight">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-medium">
            <button onClick={() => navigate("/approach")} className="group text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5 transition-colors" data-testid="home-approach-link">
              How we work — the six-stage approach <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
            <button onClick={() => navigate("/trust")} className="group text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5 transition-colors" data-testid="why-trust-link">
              How we validate every recommendation <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <Eyebrow>The difference</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Before and after JCA Matrix™</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="before-after">
            <div className="rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-md transition-shadow duration-300">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Without JCA Matrix</div>
              <ul className="mt-5 space-y-3.5">
                {["Evidence scattered across teams and vendors", "Comparator strategy discovered at scoping — too late", "Assessor questions arrive as surprises", "Readiness is a feeling, not a governed score", "Evidence spend without a prioritized business case"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-600 text-sm"><XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-blue-600 bg-white p-8 shadow-[0_20px_60px_-20px_rgba(37,99,235,0.35)] hover:-translate-y-1 transition-transform duration-300">
              <div className="text-xs font-semibold uppercase tracking-widest text-blue-600">With JCA Matrix</div>
              <ul className="mt-5 space-y-3.5">
                {["Evidence governance across the full lifecycle", "Predictable assessor questions — answered in advance", "Prioritized evidence investment roadmap", "Board-level confidence, backed by a traceable score", "Executive decision support at every governance gate"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-800 text-sm font-medium"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Representative engagement */}
      <section className="py-24" data-testid="case-study">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>A representative engagement</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              From governance doubt to a defensible plan — <span className="text-blue-600">in 12 weeks</span>
            </h2>
          </div>
          <div className="mt-12 rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
              <div className="p-8">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">The situation</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  A Phase II rare-oncology asset, single-arm pivotal trial, heading for accelerated approval. Leadership
                  believed the package was strong — but no one could say how it would fare under JCA scrutiny.
                </p>
              </div>
              <div className="p-8">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">What we found</div>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  {["No usable comparator data for the PICOs assessors would set", "HRQoL and caregiver outcomes entirely absent", "The planned indirect comparison would collapse under ESS scrutiny"].map((t) => (
                    <li key={t} className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /> {t}</li>
                  ))}
                </ul>
              </div>
              <div className="p-8 bg-slate-50/60">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">The outcome</div>
                <div className="mt-3 flex items-center gap-3">
                  <span className="font-display text-3xl font-bold text-slate-400">54</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                    <motion.div initial={{ width: "54%" }} whileInView={{ width: "81%" }} viewport={{ once: true }}
                      transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }} className="h-full rounded-full bg-emerald-500" />
                  </div>
                  <span className="font-display text-3xl font-bold text-emerald-600">81</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 text-center">JCA readiness score</div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {["Comparator gap closed before protocol lock", "HRQoL measures added while the trial could still carry them", "Board approved a sequenced €-prioritized evidence roadmap"].map((t) => (
                    <li key={t} className="flex items-start gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="px-8 py-3 border-t border-slate-100 bg-white">
              <p className="text-[11px] text-slate-400">Illustrative engagement — figures anonymized and representative of a typical JCA Readiness Assessment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final conversion */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-testid="final-conversion">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 25% 20%, #3b82f6 0%, transparent 45%), radial-gradient(circle at 80% 80%, #3b82f6 0%, transparent 40%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800/60 px-3 py-1 text-xs font-medium text-slate-300 mb-8">
            <ShieldCheck className="h-3.5 w-3.5 text-blue-400" /> Confidential · Fixed scope · 2–4 weeks
          </motion.div>
          <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
            Know where your JCA will fail —<br className="hidden sm:block" /> <span className="text-blue-400">before assessors do.</span>
          </motion.h2>
          <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
            className="mt-6 text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            One engagement. One executive verdict. One evidence roadmap.
          </motion.p>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3} className="mt-9">
            <Button data-testid="final-conversion-cta" onClick={() => navigate("/contact")}
              className="group bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-13 px-8 py-6 text-base font-medium shadow-lg shadow-blue-600/25 transition-colors duration-200">
              Request a JCA Evidence Stress Test <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
